// ============================================================
// GEN-276 (DRP H-29/H-30) — configuración de la cuenta en tiempo de ejecución.
//
// El mismo build sirve a cualquier cuenta AWS: lo que cambia entre cuentas
// (dominio de la API, dominio del hosted UI y client id de Cognito) NO va
// compilado. El despliegue escribe `config.json` junto al build con valores
// que lee por nombre de SSM (los escribe Terraform), y la SPA lo carga en
// main.jsx ANTES de montar nada, es decir, antes de iniciar la autenticación.
//
// Solo ids públicos (el app client de la SPA no tiene secreto; la protección
// es PKCE). Nunca secretos aquí.
//
// Fuente de los valores:
//  - `config.json` válido  -> manda siempre.
//  - sin él, en mock o en desarrollo (`npm run dev`) -> variables VITE_* del
//    entorno (`.env.local`), como siempre.
//  - sin él, en un build de producción real -> error visible. Nunca se
//    adivina: un id de otra cuenta daría un login roto sin explicación.
// ============================================================

export const CONFIG_URL = '/config.json'

const CLAVES = ['apiBase', 'cognitoDomain', 'cognitoClientId']

let cargada = null // { apiBase, cognitoDomain, cognitoClientId } cuando hay config.json

function deEntorno(env) {
  return {
    apiBase: env.VITE_API_BASE || '/v1',
    cognitoDomain: env.VITE_COGNITO_DOMAIN || '',
    cognitoClientId: env.VITE_COGNITO_CLIENT_ID || '',
  }
}

/** Valida el JSON de config.json. Devuelve el objeto limpio o lanza Error. */
export function validarConfig(json) {
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    throw new Error('config.json no es un objeto')
  }
  const out = {}
  for (const k of CLAVES) {
    const v = json[k]
    if (typeof v !== 'string' || !v.trim()) throw new Error(`config.json: falta «${k}»`)
    out[k] = v.trim().replace(/\/+$/, '')
  }
  if (!/^https:\/\//.test(out.cognitoDomain)) {
    throw new Error('config.json: «cognitoDomain» debe empezar por https://')
  }
  if (!/^https:\/\//.test(out.apiBase) && !out.apiBase.startsWith('/')) {
    throw new Error('config.json: «apiBase» debe ser https://… o una ruta relativa')
  }
  return out
}

/**
 * Carga config.json y lo deja disponible para getRuntimeConfig().
 * Se llama una vez, desde main.jsx, antes de montar la app.
 * `fetchFn` y `env` son inyectables solo para los tests.
 */
export async function loadRuntimeConfig({
  fetchFn = (...a) => fetch(...a),
  env = import.meta.env,
} = {}) {
  let motivo
  try {
    // no-store: tras un despliegue o un cambio de cuenta no se puede servir uno viejo.
    const r = await fetchFn(CONFIG_URL, { cache: 'no-store', headers: { Accept: 'application/json' } })
    if (!r.ok) throw new Error(`config.json respondió ${r.status}`)
    cargada = validarConfig(await r.json())
    return cargada
  } catch (e) {
    motivo = e
  }
  // Mock o desarrollo: el servidor de Vite devuelve index.html en /config.json
  // (no es JSON) y se sigue con el entorno, como antes de GEN-276.
  if (env.VITE_USE_MOCK !== 'false' || env.DEV) {
    cargada = null
    return deEntorno(env)
  }
  throw new Error(`No se pudo cargar la configuración del Pórtico: ${motivo.message}`)
}

/** Configuración vigente: config.json si se cargó; si no, el entorno (tests, mock, dev). */
export function getRuntimeConfig(env = import.meta.env) {
  return cargada || deEntorno(env)
}

/** Solo para tests: olvida lo cargado. */
export function _resetRuntimeConfig() {
  cargada = null
}
